import { useState, type ClipboardEvent, type DragEvent } from 'react'
import { toast } from 'sonner'
import { ChatbotService, type Attachment, type PexelsImage } from '../../client'
import { MAX_ATTACHMENTS, fileError, kindFor } from '../../lib/attachments'
import { useAuth } from '../../providers/auth/useAuth'

type Upload = {
  id: string
  shown: Attachment
  attachment: Attachment | null
  failed: boolean
}

function hasFiles(event: DragEvent) {
  return event.dataTransfer.types.includes('Files')
}

function revoke(removed: Upload[]) {
  for (const { shown } of removed) {
    if (shown.url.startsWith('blob:')) URL.revokeObjectURL(shown.url)
  }
}

export function useComposerAttachments(disabled: boolean) {
  const { brand } = useAuth()
  const [items, setItems] = useState<Upload[]>([])
  const [dragging, setDragging] = useState(false)

  function update(id: string, change: Partial<Upload>) {
    setItems((current) => current.map((item) => (item.id === id ? { ...item, ...change } : item)))
  }

  function start(shown: Omit<Attachment, 'asset_id'>, save: (brandId: number) => Promise<Attachment>) {
    if (!brand) return
    const id = `local:${crypto.randomUUID()}`
    setItems((current) => [...current, { id, shown: { ...shown, asset_id: id }, attachment: null, failed: false }])
    void save(brand.id).then(
      (attachment) => update(id, { attachment }),
      () => {
        update(id, { failed: true })
        toast.error(`Couldn't upload ${shown.original_filename}.`)
      },
    )
  }

  function addFiles(files: File[]) {
    if (disabled) return
    const valid = files.filter((file) => {
      const problem = fileError(file)
      if (problem) toast.error(`${file.name}: ${problem}`)
      return !problem
    })
    const room = Math.max(MAX_ATTACHMENTS - items.length, 0)
    if (valid.length > room) toast.error(`Up to ${MAX_ATTACHMENTS} files per message.`)

    for (const file of valid.slice(0, room)) {
      const shown = {
        url: URL.createObjectURL(file),
        mime_type: file.type,
        kind: kindFor(file.type),
        original_filename: file.name,
      }
      start(shown, (brandId) => ChatbotService.uploadAsset(brandId, { file }))
    }
  }

  function addPexels(image: PexelsImage, name: string) {
    start({ url: image.thumbnail, mime_type: 'image/jpeg', kind: 'image', original_filename: name }, (brandId) =>
      ChatbotService.savePexels(brandId, { url: image.url, kind: 'image', original_filename: name }),
    )
  }

  function addSaved(attachment: Attachment) {
    if (items.some((item) => item.attachment?.asset_id === attachment.asset_id)) return
    setItems((current) => [...current, { id: attachment.asset_id, shown: attachment, attachment, failed: false }])
  }

  function remove(id: string) {
    revoke(items.filter((item) => item.id === id))
    setItems((current) => current.filter((item) => item.id !== id))
  }

  function clear() {
    revoke(items)
    setItems([])
  }

  const dropZone = {
    onDragOver(event: DragEvent<HTMLElement>) {
      if (!hasFiles(event)) return
      event.preventDefault()
      setDragging(!disabled)
    },
    onDragLeave(event: DragEvent<HTMLElement>) {
      if (!event.currentTarget.contains(event.relatedTarget as Node | null)) setDragging(false)
    },
    onDrop(event: DragEvent<HTMLElement>) {
      if (!hasFiles(event)) return
      event.preventDefault()
      setDragging(false)
      addFiles([...event.dataTransfer.files])
    },
  }

  function paste(event: ClipboardEvent<HTMLTextAreaElement>) {
    const files = [...event.clipboardData.files]
    if (files.length === 0) return
    event.preventDefault()
    addFiles(files)
  }

  return {
    items,
    attachments: items.flatMap(({ attachment }) => (attachment ? [attachment] : [])),
    uploading: items.some(({ attachment, failed }) => !attachment && !failed),
    full: items.length >= MAX_ATTACHMENTS,
    dragging,
    dropZone,
    paste,
    addFiles,
    addPexels,
    addSaved,
    remove,
    clear,
  }
}

export type ComposerUploads = ReturnType<typeof useComposerAttachments>
